import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import axios from "./axios";

/**
 * Journaux — audit trail + system logs.
 *
 * TWO STORES, ONE SCREEN
 * ----------------------
 * `audit_logs` answers "who did what" (compliance record, kept for years).
 * `system_logs` answers "what broke" (diagnostics, kept for weeks). They are
 * separate tables on the backend for good reason, but an operator
 * investigating an incident needs both on the same screen at the same
 * timestamp — reconstructing that correlation by hand across two tools is
 * exactly what wastes the first hour of every incident.
 *
 * Everything here is READ-ONLY. There is no mutation hook because there is no
 * mutation endpoint: audit rows are immutable server-side, and the only
 * removal path is the scheduled `audit:prune` retention sweep.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type AuditCategory =
  | "data"
  | "auth"
  | "authz"
  | "billing"
  | "export"
  | "system";

export type AuditSeverity = "info" | "notice" | "warning" | "critical";

export interface AuditLog {
  id: number;
  request_id: string | null;
  action: string;
  category: AuditCategory;
  severity: AuditSeverity;
  auditable_type: string | null;
  auditable_id: number | null;
  auditable_label: string | null;
  /** Human name of the model ("Societe"), resolved server-side. */
  subject_label: string | null;
  actor_id: number | null;
  actor_type: "user" | "system" | "api" | "guest";
  actor_email: string | null;
  actor_name: string | null;
  actor_roles: string | null;
  company_id: number | null;
  /** Only the fields that changed, already redacted. */
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  changed_fields: string[];
  message: string | null;
  context: Record<string, any> | null;
  endpoint: string | null;
  method: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string | null;
}

export interface SystemLog {
  id: number;
  level: "error" | "warning" | "info" | "debug";
  type: string;
  endpoint: string | null;
  method: string | null;
  message: string;
  context: Record<string, any> | null;
  request_data: Record<string, any> | null;
  user_id: number | null;
  ip_address: string | null;
  user_agent: string | null;
  stack_trace: string | null;
  file_path: string | null;
  line_number: number | null;
  created_at: string | null;
}

export interface AuditFilters {
  q?: string;
  category?: string;
  severity?: string;
  action?: string;
  actor_id?: number | string;
  company_id?: number | string;
  auditable_type?: string;
  auditable_id?: number | string;
  request_id?: string;
  field?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface SystemLogFilters {
  q?: string;
  level?: string;
  type?: string;
  user_id?: number | string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface AuditStats {
  total: number;
  last_24h: number;
  critical_7d: number;
  failed_logins_24h: number;
  by_category: Record<string, number>;
  by_severity: Record<string, number>;
  timeline: { day: string; total: number }[];
  top_actors: {
    actor_id: number;
    actor_email: string | null;
    actor_name: string | null;
    total: number;
  }[];
}

export interface AuditFilterOptions {
  actions: string[];
  categories: Record<string, string>;
  severities: AuditSeverity[];
  types: { value: string; label: string }[];
  actors: {
    actor_id: number;
    actor_email: string | null;
    actor_name: string | null;
  }[];
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const auditKeys = {
  all: ["audit"] as const,
  logs: (f: AuditFilters) => ["audit", "logs", f] as const,
  log: (id: number) => ["audit", "log", id] as const,
  stats: (f: AuditFilters) => ["audit", "stats", f] as const,
  filters: ["audit", "filters"] as const,
  subject: (type: string, id: number) => ["audit", "subject", type, id] as const,
  system: (f: SystemLogFilters) => ["audit", "system", f] as const,
  systemLog: (id: number) => ["audit", "system", "log", id] as const,
  systemStats: ["audit", "system", "stats"] as const,
};

/**
 * Strip empty values before they reach the query string.
 *
 * An empty `category=` is not the same as no category to the backend filter,
 * and sending it would also fragment the React Query cache key — the same
 * view would be cached separately depending on whether a filter had ever been
 * touched and cleared.
 */
const clean = (params: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );

// ── Audit trail ──────────────────────────────────────────────────────────────

export const useAuditLogs = (filters: AuditFilters = {}) => {
  const { page = 1, per_page = 25, ...rest } = filters;

  const { data, ...query } = useQuery({
    queryKey: auditKeys.logs({ ...rest, page, per_page }),
    queryFn: async (): Promise<{ data: AuditLog[]; pagination: Pagination }> => {
      const res = await axios.get("/admin/audit-logs", {
        params: clean({ ...rest, page, per_page }),
      });

      return {
        data: res.data?.data ?? [],
        pagination: {
          current_page: res.data?.current_page ?? 1,
          per_page: res.data?.per_page ?? per_page,
          total: res.data?.total ?? 0,
          last_page: res.data?.last_page ?? 1,
        },
      };
    },
    /*
     * The trail is append-only, so a cached page can never go stale in the
     * usual sense — an existing row will never change. Only NEW rows appear.
     * Keeping the previous page visible during a refetch avoids the table
     * blanking on every filter change.
     */
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });

  return { logs: data?.data ?? [], pagination: data?.pagination, ...query };
};

export const useAuditLog = (id: number | null) =>
  useQuery({
    queryKey: auditKeys.log(id ?? 0),
    queryFn: async (): Promise<{
      log: AuditLog;
      /** Other rows from the same HTTP request — the rest of the story. */
      related: AuditLog[];
      /** Everything else that ever happened to this record. */
      history: AuditLog[];
    }> => {
      const res = await axios.get(`/admin/audit-logs/${id}`);
      return res.data?.data;
    },
    enabled: !!id,
  });

export const useAuditStats = (filters: AuditFilters = {}) => {
  const { page, per_page, ...rest } = filters;

  const { data, ...query } = useQuery({
    queryKey: auditKeys.stats(rest),
    queryFn: async (): Promise<AuditStats> => {
      const res = await axios.get("/admin/audit-logs/stats", {
        params: clean(rest),
      });
      return res.data?.data;
    },
    staleTime: 30_000,
  });

  return { stats: data ?? null, ...query };
};

export const useAuditFilterOptions = () => {
  const { data, ...query } = useQuery({
    queryKey: auditKeys.filters,
    queryFn: async (): Promise<AuditFilterOptions> => {
      const res = await axios.get("/admin/audit-logs/filters");
      return res.data?.data;
    },
    // Actions and actors change slowly; refetching them on every mount would
    // run four GROUP BYs over the largest table in the schema for nothing.
    staleTime: 5 * 60_000,
  });

  return { options: data ?? null, ...query };
};

/** History for one record — powers a "Historique" panel on any detail screen. */
export const useSubjectHistory = (
  type: string | null,
  id: number | null,
  perPage = 50
) =>
  useQuery({
    queryKey: auditKeys.subject(type ?? "", id ?? 0),
    queryFn: async (): Promise<AuditLog[]> => {
      const res = await axios.get(`/admin/audit-logs/subject/${type}/${id}`, {
        params: { per_page: perPage },
      });
      return res.data?.data ?? [];
    },
    enabled: !!type && !!id,
  });

/**
 * Download the current filter as CSV.
 *
 * Not a React Query mutation: the result is a file, not state, and caching a
 * 40 MB blob would be actively harmful. Uses the axios instance rather than a
 * plain link so the X-token-api header travels with it — an <a href> would hit
 * the endpoint unauthenticated and 401.
 */
export const downloadAuditCsv = async (
  filters: AuditFilters = {},
  errorLabel = "Échec de l'export"
): Promise<void> => {
  const { page, per_page, ...rest } = filters;

  try {
    const res = await axios.get("/admin/audit-logs/export", {
      params: clean(rest),
      responseType: "blob",
      // A large export is streamed and chunked server-side; the default
      // client timeout would abort a download the server is still writing.
      timeout: 10 * 60 * 1000,
    });

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `audit-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    /*
     * responseType: "blob" means an error body arrives as a Blob too, so the
     * 422 "export too large — narrow your filters" message is unreadable
     * without parsing it back to text. That message is the entire remedy, so
     * it is worth the extra step rather than showing a generic failure.
     */
    let message = errorLabel;
    try {
      const text = await err?.response?.data?.text?.();
      if (text) message = JSON.parse(text)?.message ?? message;
    } catch {
      /* fall through to the generic label */
    }
    toast.error(message);
  }
};

// ── System logs ──────────────────────────────────────────────────────────────

export const useSystemLogs = (filters: SystemLogFilters = {}) => {
  const { page = 1, per_page = 25, ...rest } = filters;

  const { data, ...query } = useQuery({
    queryKey: auditKeys.system({ ...rest, page, per_page }),
    queryFn: async (): Promise<{ data: SystemLog[]; pagination: Pagination }> => {
      const res = await axios.get("/admin/system-logs", {
        params: clean({ ...rest, page, per_page }),
      });

      return {
        data: res.data?.data ?? [],
        pagination: {
          current_page: res.data?.current_page ?? 1,
          per_page: res.data?.per_page ?? per_page,
          total: res.data?.total ?? 0,
          last_page: res.data?.last_page ?? 1,
        },
      };
    },
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });

  return { logs: data?.data ?? [], pagination: data?.pagination, ...query };
};

export const useSystemLogStats = () => {
  const { data, ...query } = useQuery({
    queryKey: auditKeys.systemStats,
    queryFn: async () => {
      const res = await axios.get("/admin/system-logs/stats");
      return res.data?.data as {
        total: number;
        errors_24h: number;
        by_level: Record<string, number>;
        by_type: Record<string, number>;
        timeline: { day: string; total: number }[];
        levels: string[];
        types: string[];
      };
    },
    staleTime: 60_000,
  });

  return { stats: data ?? null, ...query };
};

// ── Presentation helpers ─────────────────────────────────────────────────────

/** MUI palette colour for a severity. Shared so the chips and KPIs agree. */
export const severityColor = (
  severity: AuditSeverity
): "default" | "info" | "warning" | "error" => {
  switch (severity) {
    case "critical":
      return "error";
    case "warning":
      return "warning";
    case "notice":
      return "info";
    default:
      return "default";
  }
};

export const levelColor = (
  level: string
): "default" | "info" | "warning" | "error" => {
  switch (level) {
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "default";
  }
};

/**
 * Turn an action key into something readable.
 *
 * Falls back to the raw key rather than to "Unknown": a new action added
 * server-side should show as `system.retention_sweep` — ugly but truthful and
 * searchable — instead of being flattened into a label that hides what it was.
 */
export const ACTION_LABELS: Record<string, string> = {
  created: "Création",
  updated: "Modification",
  deleted: "Suppression",
  soft_deleted: "Archivage",
  restored: "Restauration",
  "auth.login": "Connexion",
  "auth.login_failed": "Échec de connexion",
  "auth.logout": "Déconnexion",
  "auth.password_reset": "Réinitialisation mot de passe",
  "authz.denied": "Accès refusé",
  "authz.role_assigned": "Rôle attribué",
  "authz.role_revoked": "Rôle retiré",
  "authz.role_permissions_changed": "Permissions d'un rôle modifiées",
  "export.download": "Export",
  "system.import": "Import",
  "system.retention_sweep": "Purge des journaux",
};

export const actionLabel = (action: string): string =>
  ACTION_LABELS[action] ?? action;
